import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, TextField, Chip, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Collapse, Tooltip, Button, LinearProgress, FormControlLabel, Checkbox,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';

import { serverResourcesAPI, serverServicesAPI } from '../services/api';
import VpnPanel from '../components/VpnPanel';
import DirectConnectPanel from '../components/DirectConnectPanel';
import ResourceChart, { niceMax } from '../components/ResourceChart';
import Spark from '../components/Spark';
import ServiceJobsDialog from '../components/ServiceJobsDialog';
import PrintingServiceDialog from '../components/PrintingServiceDialog';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const WARN = 80;
const CRITICAL = 90;
const RANGES = { '24h': 'Last 24 hours', '7d': 'Last 7 days', '30d': 'Last 30 days' };
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const level = (v) => (v == null ? 'default' : v >= CRITICAL ? 'error' : v >= WARN ? 'warning' : 'success');
const SERVICE_KEYS = ['manager', 'printing'];
const SERVICE_LABEL = { manager: 'STARLIMS Manager Service', printing: 'STARLIMS Report Printing Service' };   // the Manager Service is called ... R2 on newer systems: both are looked for
const SERVICE = SERVICE_LABEL.manager;
const slot = (host, key) => `${host}|${key}`;
const CONCURRENT_CHECKS = 3;
const serviceView = (r) => (!r.found ? { label: 'Not installed', color: 'default' }
  : r.state === 'Running' ? { label: 'Running', color: 'success' }
    : r.state === 'Stopped' ? { label: 'Stopped', color: 'error' } : { label: r.state, color: 'warning' });
const pct = (v) => (v == null ? '-' : `${v}%`);
const EXTRA_COLOR = '#00838f';
// Other readings of a server (page file, disk latency, ...). The chart scale is 0-100 for percentages, otherwise a tidy top above the highest reading.
const extraLevel = (def, item) => (!item || item.latest == null ? 'default' : def.critical != null && item.latest >= def.critical ? 'error' : def.warn != null && item.latest >= def.warn ? 'warning' : 'success');
const extraMax = (def, item) => (def.unit === '%' ? 100 : niceMax(Math.max(item.peak, def.critical || 0, def.warn || 0)));
const reading = (def, v) => (v == null ? '-' : `${v.toLocaleString()}${def.unit}`);
const flagged = (server, defs) => defs.map((d) => ({ def: d, item: server.extras?.[d.key] })).filter((x) => x.item && ['warning', 'error'].includes(extraLevel(x.def, x.item)));

function Pressure({ server, defs }) {
  const hits = flagged(server, defs);
  if (!hits.length) return <Typography variant="body2" color="text.secondary">{Object.keys(server.extras || {}).length ? 'None' : '-'}</Typography>;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {hits.map(({ def, item }) => (
        <Tooltip key={def.key} title={<>{`${def.label}: now ${reading(def, item.latest)}, average ${reading(def, item.average)}, highest ${reading(def, item.peak)}.`}{def.help && <><br /><br />{def.help}</>}</>}>
          <Chip size="small" color={extraLevel(def, item)} label={`${def.label.split(' (')[0]} ${reading(def, item.latest)}`} />
        </Tooltip>
      ))}
    </Box>
  );
}

function Reading({ item }) {
  if (!item) return <Typography variant="body2" color="text.secondary">-</Typography>;
  return (
    <Tooltip title={`Average ${pct(item.average)}, highest ${pct(item.peak)}`}>
      <Chip size="small" color={level(item.latest)} label={pct(item.latest)} />
    </Tooltip>
  );
}

function ServiceCell({ entry, serviceKey, onCheck, onOpen }) {
  if (entry?.loading) return <CircularProgress size={18} />;
  if (entry?.error) return <Tooltip title={entry.error}><Chip size="small" variant="outlined" color="warning" label="Could not check" onClick={onCheck} /></Tooltip>;
  if (entry?.result) {
    const v = serviceView(entry.result);
    const found = entry.result.found;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={found ? `${entry.result.service}: startup ${entry.result.start_mode || 'unknown'}. Checked ${new Date(entry.result.checked_at).toLocaleTimeString()}. Click to see ${serviceKey === 'printing' ? 'its settings and waiting jobs' : 'the jobs it is running'}.` : `${SERVICE_LABEL[serviceKey]} is not installed on this server. Click to check again.`}>
          <Chip size="small" color={v.color} label={v.label} onClick={found ? onOpen : onCheck} />
        </Tooltip>
        {found && <Tooltip title="Check the service again"><IconButton size="small" aria-label="Check the service again" onClick={onCheck}><RefreshIcon fontSize="inherit" /></IconButton></Tooltip>}
      </Box>
    );
  }
  return <Button size="small" onClick={onCheck}>Check</Button>;
}

// What the server is in AWS (instance type and size). Read when the row is opened; a failure only hides this block, the charts still show.
function InstanceDetails({ customerCode, host }) {
  const [info, setInfo] = useState(null);
  const [problem, setProblem] = useState('');
  useEffect(() => {
    let live = true;
    serverResourcesAPI.instance(customerCode, host).then((res) => live && setInfo(res.data)).catch((err) => live && setProblem(errText(err, 'Could not read the instance from AWS')));
    return () => { live = false; };
  }, [customerCode, host]);
  if (problem) return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 1 }}>Instance details: {problem}</Typography>;
  if (!info) return <LinearProgress sx={{ mt: 1 }} aria-label="Loading instance details" />;
  const facts = [
    ['Instance type', info.instance_type], ['vCPUs', info.size_known ? info.vcpus : 'Not available'], ['Memory', info.size_known ? `${info.memory_gib} GiB` : 'Not available'], ['vCPU speed', info.ghz ? `${info.ghz} GHz` : 'Not available'], ['Network', info.network],
    ['State', info.state], ['Zone', info.zone], ['EBS volumes', info.volumes],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 2, pt: 2 }} aria-label={`Instance details for ${host}`}>
      {facts.map(([label, value]) => (
        <Box key={label}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="body2" sx={{ fontFamily: label === 'Instance type' ? 'monospace' : undefined, wordBreak: 'break-word' }}>{String(value)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function ServerRow({ server, customerCode, timestamps, rangeLabel, services, onCheck, onOpen, extras }) {
  const [open, setOpen] = useState(false);
  const [chart, setChart] = useState(null);
  const show = (title, values, color, scale) => () => setChart({ title, values, color, ...scale });
  const shownExtras = extras.filter((d) => server.extras?.[d.key]);
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={`Show charts for ${server.host}`} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace' }}>{server.host}{server.database && <Chip size="small" variant="outlined" color="info" label="Database" sx={{ ml: 1, fontFamily: 'inherit' }} />}</TableCell>
        <TableCell>{server.environment || '-'}</TableCell>
        <TableCell><Reading item={server.cpu} /></TableCell>
        <TableCell><Reading item={server.memory} /></TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {server.disks.length === 0 && '-'}
            {server.disks.map((d) => (
              <Tooltip key={d.device} title={`${d.device} average ${pct(d.average)}, highest ${pct(d.peak)}`}>
                <Chip size="small" color={level(d.latest)} label={`${d.device} ${pct(d.latest)}`} />
              </Tooltip>
            ))}
          </Box>
        </TableCell>
        <TableCell><Pressure server={server} defs={extras} /></TableCell>
        {SERVICE_KEYS.map((key) => (
          <TableCell key={key}><ServiceCell entry={services[slot(server.host, key)]} serviceKey={key} onCheck={() => onCheck(server.host, key)} onOpen={() => onOpen(server.host, key)} /></TableCell>
        ))}
        <TableCell>{server.reporting ? <Chip size="small" variant="outlined" color="success" label="Reporting" /> : <Chip size="small" color="warning" label="Not reporting" />}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <InstanceDetails customerCode={customerCode} host={server.host} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2, py: 2 }}>
              {server.cpu && <Spark title="CPU used" values={server.cpu.series} timestamps={timestamps} color="#1976d2" onOpen={show('CPU used', server.cpu.series, '#1976d2')} />}
              {server.memory && <Spark title="Memory used" values={server.memory.series} timestamps={timestamps} color="#7b1fa2" onOpen={show('Memory used', server.memory.series, '#7b1fa2')} />}
              {server.disks.map((d) => <Spark key={d.device} title={`Disk ${d.device} used`} values={d.series} timestamps={timestamps} color="#2e7d32" onOpen={show(`Disk ${d.device} used`, d.series, '#2e7d32')} />)}
            </Box>
            {shownExtras.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>More readings</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2, py: 1 }}>
                  {shownExtras.map((d) => {
                    const item = server.extras[d.key];
                    const scale = { unit: d.unit, max: extraMax(d, item), warn: d.warn, critical: d.critical };
                    const label = `${d.label}: now ${reading(d, item.latest)}`;
                    return <Spark key={d.key} title={label} values={item.series} timestamps={timestamps} color={EXTRA_COLOR} {...scale} help={d.help} onOpen={show(d.label, item.series, EXTRA_COLOR, scale)} />;
                  })}
                </Box>
              </>
            )}
            {server.last_seen && <Typography variant="caption" color="text.secondary">Last reading {new Date(server.last_seen).toLocaleString()}. Dashed lines mark {WARN}% and {CRITICAL}%. Click a chart to enlarge it.</Typography>}
            {chart && <ResourceChart title={chart.title} subtitle={`${server.host} - ${rangeLabel}`} values={chart.values} timestamps={timestamps} color={chart.color} unit={chart.unit} max={chart.max} warn={chart.warn} critical={chart.critical} onClose={() => setChart(null)} />}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function ServerResources() {
  const [customers, setCustomers] = useState([]);
  const [code, setCode] = useState('');
  const [range, setRange] = useState('24h');
  const [environment, setEnvironment] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [services, setServices] = useState({});
  const [checkingAll, setCheckingAll] = useState(false);
  const [openService, setOpenService] = useState(null);
  const [checkKey, setCheckKey] = useState('manager');
  const [includeOffline, setIncludeOffline] = useState(false);
  const [onlyInstalled, setOnlyInstalled] = useState(false);

  useEffect(() => {
    serverResourcesAPI.customers().then((res) => setCustomers(res.data)).catch((err) => setError(errText(err, 'Could not load customers')));
  }, []);

  useEffect(() => { setServices({}); setOnlyInstalled(false); }, [code]);

  const checkService = useCallback(async (host, key = 'manager') => {
    const id = slot(host, key);
    setServices((cur) => ({ ...cur, [id]: { loading: true } }));
    try {
      const res = await serverServicesAPI.status(code, host, key);
      setServices((cur) => ({ ...cur, [id]: { result: res.data } }));
    } catch (err) {
      setServices((cur) => ({ ...cur, [id]: { error: errText(err, 'Could not check the service') } }));
    }
  }, [code]);

  const load = useCallback(async () => {
    if (!code) { setData(null); return; }
    setLoading(true); setError(null);
    try { setData((await serverResourcesAPI.get(code, range)).data); }
    catch (err) { setData(null); setError(errText(err, 'Could not load server resources')); }
    finally { setLoading(false); }
  }, [code, range]);

  useEffect(() => { load(); }, [load]);

  const environments = useMemo(() => [...new Set((data?.servers || []).map((s) => s.environment).filter(Boolean))], [data]);
  const servers = (data?.servers || []).filter((s) => !environment || s.environment === environment);
  const targets = servers.filter((s) => includeOffline || s.reporting);
  const summary = servers.reduce((c, s) => {
    const e = services[slot(s.host, checkKey)];
    const key = e?.result ? (!e.result.found ? 'missing' : e.result.state === 'Running' ? 'running' : e.result.state === 'Stopped' ? 'stopped' : 'other') : e?.error ? 'failed' : null;
    return key ? { ...c, [key]: (c[key] || 0) + 1 } : c;
  }, {});
  const checked = servers.filter((s) => services[slot(s.host, checkKey)]?.result || services[slot(s.host, checkKey)]?.error).length;
  const shownServers = onlyInstalled ? servers.filter((s) => services[slot(s.host, checkKey)]?.result?.found) : servers;

  const checkAll = async () => {
    const queue = targets.map((s) => s.host);
    setCheckingAll(true);
    const worker = async () => { while (queue.length) await checkService(queue.shift(), checkKey); };
    await Promise.all(Array.from({ length: CONCURRENT_CHECKS }, worker));
    setCheckingAll(false);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Server Resources</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A customer's VPN and Direct Connect links (from AWS) and the CPU, memory and disk use of its servers (from Sumo Logic). Colours turn orange at {WARN}% and red at {CRITICAL}%. "Under pressure" lists other readings that are over their warning level (page file, disk latency and queue, garbage collection, processor queue). Open a row for the instance type and size, the charts and the rest of the readings.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Customer" value={code} onChange={(e) => { setCode(e.target.value); setEnvironment(''); }} sx={{ minWidth: 280 }} slotProps={NATIVE}>
          <option value="">Select a customer</option>
          {customers.map((c) => <option key={c.id} value={c.cust_code}>{c.cust_code} - {c.cust_name}</option>)}
        </TextField>
        <TextField select size="small" label="Environment" value={environment} onChange={(e) => setEnvironment(e.target.value)} sx={{ minWidth: 160 }} slotProps={NATIVE} disabled={!environments.length}>
          <option value="">All</option>
          {environments.map((e) => <option key={e} value={e}>{e}</option>)}
        </TextField>
        <ToggleButtonGroup size="small" exclusive value={range} onChange={(e, v) => v && setRange(v)} aria-label="Time range">
          {Object.entries(RANGES).map(([k, label]) => <ToggleButton key={k} value={k}>{label}</ToggleButton>)}
        </ToggleButtonGroup>
      </Paper>

      {code && <VpnPanel customerCode={code} />}
      {code && <DirectConnectPanel customerCode={code} />}
      {!loading && data && servers.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ flexGrow: 1, minWidth: 260 }}>
              <Typography variant="subtitle1" fontWeight="bold">Find a service</Typography>
              <Typography variant="body2" color="text.secondary">Not sure which server runs it? Check every server for this customer in one go.{checkKey === 'manager' ? ' Both "STARLIMS Manager Service" and "STARLIMS Manager Service R2" are looked for.' : ''}</Typography>
            </Box>
            <TextField select size="small" label="Service" value={checkKey} onChange={(e) => { setCheckKey(e.target.value); setOnlyInstalled(false); }} sx={{ minWidth: 260 }} slotProps={NATIVE}>
              {SERVICE_KEYS.map((k) => <option key={k} value={k}>{SERVICE_LABEL[k]}</option>)}
            </TextField>
            <FormControlLabel control={<Checkbox size="small" checked={includeOffline} onChange={(e) => setIncludeOffline(e.target.checked)} />} label="Include servers not reporting" />
            <Button variant="contained" onClick={checkAll} disabled={checkingAll || targets.length === 0}>
              {checkingAll ? 'Checking...' : `Check all servers (${targets.length})`}
            </Button>
          </Box>
          {checkingAll && <LinearProgress variant="determinate" value={(checked / Math.max(targets.length, 1)) * 100} sx={{ mt: 1.5 }} />}
          {checked > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
              <Typography variant="body2">Checked {checked} of {servers.length}:</Typography>
              {summary.running > 0 && <Chip size="small" color="success" label={`Running: ${summary.running}`} />}
              {summary.stopped > 0 && <Chip size="small" color="error" label={`Stopped: ${summary.stopped}`} />}
              {summary.other > 0 && <Chip size="small" color="warning" label={`Other state: ${summary.other}`} />}
              {summary.missing > 0 && <Chip size="small" variant="outlined" label={`Not installed: ${summary.missing}`} />}
              {summary.failed > 0 && <Chip size="small" variant="outlined" color="warning" label={`Could not check: ${summary.failed}`} />}
              <FormControlLabel sx={{ ml: 1 }} control={<Checkbox size="small" checked={onlyInstalled} onChange={(e) => setOnlyInstalled(e.target.checked)} />} label="Show only servers where it is installed" />
            </Box>
          )}
        </Paper>
      )}

      {!code && <Typography color="text.secondary">Choose a customer to see its servers.</Typography>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {!loading && data && (
        servers.length === 0
          ? <Alert severity="info">No server metrics were found in Sumo Logic for {data.customer} in this period.</Alert>
          : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Server</TableCell>
                    <TableCell>Environment</TableCell>
                    <TableCell>CPU now</TableCell>
                    <TableCell>Memory now</TableCell>
                    <TableCell>Disks now</TableCell>
                    <TableCell><Tooltip title="Readings that are over their warning level (orange) or critical level (red): page file, disk latency, disk queue, garbage collection and processor queue. Hover a chip for what it means; open the row to see all readings."><span>Under pressure</span></Tooltip></TableCell>
                    {SERVICE_KEYS.map((k) => <TableCell key={k}>{SERVICE_LABEL[k]}</TableCell>)}
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shownServers.map((s) => <ServerRow key={s.host} server={s} customerCode={code} timestamps={data.timestamps} rangeLabel={RANGES[data.range]} services={services} onCheck={checkService} onOpen={(host, key) => setOpenService({ host, key })} extras={data.extras || []} />)}
                </TableBody>
              </Table>
            </TableContainer>
          )
      )}
      {openService?.key === 'manager' && <ServiceJobsDialog customerCode={code} host={openService.host} service={SERVICE} onClose={() => setOpenService(null)} />}
      {openService?.key === 'printing' && <PrintingServiceDialog customerCode={code} host={openService.host} onClose={() => setOpenService(null)} />}
    </Box>
  );
}

export default ServerResources;
